import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Layout } from './components/Layout';
import { AnalyticsPage } from './pages/Analytics';
import { FeedPage } from './pages/Feed';
import { LeadDetailPage } from './pages/LeadDetail';
import { LoginPage } from './pages/Login';
import { PlaceholderPage } from './pages/Placeholder';
import { PriceMapPage } from './pages/PriceMap';
import { SettingsPage } from './pages/Settings';
import { ZonesPage } from './pages/Zones';
import { SessionProvider, useSession } from './api/session';

/**
 * Wraps every authed route. While the SessionProvider is bootstrapping
 * (calling /api/auth/me), shows a minimal loader. Anonymous users get
 * redirected to /login, preserving the original URL via location state
 * so we can bounce them back after a successful login.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useSession();
  const location = useLocation();

  if (state.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-500">
        …
      </div>
    );
  }
  if (state.status === 'anon') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

/**
 * If a logged-in user hits /login directly, bounce them to /feed instead
 * of showing the form. Avoids surprise re-login churn.
 */
function PublicOnly({ children }: { children: ReactNode }) {
  const { state } = useSession();
  if (state.status === 'authed') return <Navigate to="/feed" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicOnly>
                <LoginPage />
              </PublicOnly>
            }
          />
          <Route
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          >
            <Route path="/" element={<Navigate to="/feed" replace />} />
            <Route path="/feed" element={<FeedPage />} />
            <Route path="/leads/:id" element={<LeadDetailPage />} />
            <Route path="/zones" element={<ZonesPage />} />
            <Route path="/map" element={<PriceMapPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route
              path="*"
              element={<PlaceholderPage title="404 — δεν βρέθηκε" />}
            />
          </Route>
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}
